import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  createMember,
  listLookupValues,
} from "../../../../../Services/adminCorporates";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../../ui/dialog";
import { Button } from "../../../../../ui/button";
import { Input } from "../../../../../ui/input";
import { Label } from "../../../../../ui/label";

const schema = z.object({
  first_name: z.string().trim().min(1, "First name is required").max(100),
  last_name: z.string().trim().min(1, "Last name is required").max(100),
  work_email: z
    .string()
    .trim()
    .min(1, "Work email is required")
    .email("Enter a valid email"),
  personal_email: z
    .string()
    .trim()
    .min(1, "Personal email is required")
    .email("Enter a valid email"),
  department: z.string().trim().optional().or(z.literal("")),
  role_title: z.string().trim().optional().or(z.literal("")),
});

const Field = ({ label, htmlFor, error, children }) => (
  <div className="grid gap-1.5">
    <Label htmlFor={htmlFor}>{label}</Label>
    {children}
    {error && (
      <p className="text-xs text-destructive" role="alert">
        {error}
      </p>
    )}
  </div>
);

/**
 * Modal for adding a single member as `pending_signup`. Reuses the same field
 * set as the activation form (minus the confirm-email check, which is for
 * end-users typing their own email).
 */
const AddMemberModal = ({ open, onOpenChange, corporateId, onCreated }) => {
  const [submitError, setSubmitError] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      first_name: "",
      last_name: "",
      work_email: "",
      personal_email: "",
      department: "",
      role_title: "",
    },
  });

  // Load lookups on open so the dropdowns reflect the latest values.
  useEffect(() => {
    if (!open || !corporateId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [d, r] = await Promise.all([
          listLookupValues(corporateId, "departments", { active_only: true }),
          listLookupValues(corporateId, "roles", { active_only: true }),
        ]);
        if (cancelled) return;
        setDepartments(d?.items || []);
        setRoles(r?.items || []);
      } catch (_) {
        if (cancelled) return;
        setDepartments([]);
        setRoles([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, corporateId]);

  // Reset form when modal closes.
  useEffect(() => {
    if (!open) {
      reset();
      setSubmitError(null);
    }
  }, [open, reset]);

  const onSubmit = async (values) => {
    setSubmitError(null);
    try {
      await createMember(corporateId, {
        first_name: values.first_name,
        last_name: values.last_name,
        work_email: values.work_email,
        personal_email: values.personal_email,
        department: values.department || undefined,
        role_title: values.role_title || undefined,
      });
      onCreated?.();
      onOpenChange(false);
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err?.message || "";
      if (status === 409 || /already exists|duplicate/i.test(message)) {
        setError("work_email", {
          message: "A member with this work email already exists.",
        });
      } else {
        setSubmitError(message || "Failed to add member. Please try again.");
      }
    }
  };

  const inputClass =
    "h-10 px-3 rounded-md border border-input bg-background text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background w-full";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Add member</DialogTitle>
          <DialogDescription>
            The new member will receive an activation invite based on the work
            email.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field
              label="First name"
              htmlFor="add-fname"
              error={errors.first_name?.message}
            >
              <Input id="add-fname" {...register("first_name")} />
            </Field>
            <Field
              label="Last name"
              htmlFor="add-lname"
              error={errors.last_name?.message}
            >
              <Input id="add-lname" {...register("last_name")} />
            </Field>
          </div>

          <Field
            label="Work email"
            htmlFor="add-work-email"
            error={errors.work_email?.message}
          >
            <Input id="add-work-email" type="email" {...register("work_email")} />
          </Field>

          <Field
            label="Personal email"
            htmlFor="add-personal-email"
            error={errors.personal_email?.message}
          >
            <Input
              id="add-personal-email"
              type="email"
              {...register("personal_email")}
            />
          </Field>

          {departments.length > 0 && (
            <Field
              label="Department"
              htmlFor="add-department"
              error={errors.department?.message}
            >
              <select
                id="add-department"
                className={inputClass}
                {...register("department")}
              >
                <option value="">—</option>
                {departments.map((d) => (
                  <option key={d.id || d.name} value={d.name}>
                    {d.name}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {roles.length > 0 && (
            <Field
              label="Role"
              htmlFor="add-role"
              error={errors.role_title?.message}
            >
              <select id="add-role" className={inputClass} {...register("role_title")}>
                <option value="">—</option>
                {roles.map((r) => (
                  <option key={r.id || r.name} value={r.name}>
                    {r.name}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {submitError && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {submitError}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Adding…" : "Add member"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddMemberModal;
